import {
  resolveBillingProvider,
  isAppleManaged,
  manageSubscriptionHint,
  PROVIDER_LABEL,
  APPLE_SUBSCRIPTIONS_URL,
} from "@/lib/billing-provider";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackOperational } from "@/lib/track";
import { useIsNativeApp } from "@/lib/native-app";
import { InAppCancelMembership } from "@/components/membership/InAppCancelMembership";
import { getMyMembership } from "@/lib/membership.functions";
import { describePlan } from "@/lib/founding-plan";
import { resolveBillingTerm } from "@/lib/billing-term";

import {
  UserRound,
  UserCircle2,
  Heart,
  CalendarDays,
  BadgeCheck,
  CreditCard,
  SlidersHorizontal,
  CircleHelp,
  ArrowRight,
  Shield,
  Stethoscope,
  Mail,
  Pencil,
  Loader2,
  ThumbsUp,
  MessageSquare,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export const Route = createFileRoute("/my-membership")({
  head: () => ({
    meta: [
      { title: "My Membership — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your personal area: profile, favourites, events, membership and preferences.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MyMembershipHub,
});

type Item = {
  to: string;
  label: string;
  desc: string;
  icon: LucideIcon;
  iconClassName?: string;
  iconFill?: string;
};

const ITEMS: Item[] = [
  {
    to: "/portal",
    label: "Member Portal",
    desc: "Your payments, renewal dates and saved journal entries, all in one place.",
    icon: BadgeCheck,
  },
  {
    to: "/profile",
    label: "My Profile",
    desc: "Update your personal information, profile photo, country and preferences.",
    icon: UserRound,
  },

  {
    to: "/favorites",
    label: "My Favourites",
    desc: "Quickly access your saved tools, articles, exercises and affirmation cards.",
    icon: Heart,
    iconClassName: "text-red-500",
    iconFill: "currentColor",
  },
  {
    to: "/my-events",
    label: "My Events",
    desc: "View your upcoming workshops, sound healing sessions and live events.",
    icon: CalendarDays,
  },
  {
    to: "/membership",
    label: "Membership",
    desc: "View your membership, renewal date and subscription information.",
    icon: BadgeCheck,
  },
  {
    to: "/preferences",
    label: "Preferences",
    desc: "Manage notifications, accessibility, privacy and comfort settings.",
    icon: SlidersHorizontal,
  },
  {
    to: "/help",
    label: "Help Centre",
    desc: "Find answers to common questions or contact the Recovery Trauma team.",
    icon: CircleHelp,
  },
];

type RoleLabel = "Admin" | "Therapist" | "Member";

type Summary = {
  email: string | null;
  name: string;
  role: RoleLabel;
  joined: string | null;
  tier: string;
  avatarUrl: string | null;
};

function MyMembershipHub() {
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTimedOut(false);
    setErrorMsg(null);

    // Warn threshold (ms) — anything slower gets flagged in the console
    // and forwarded to Sentry if it's wired up.
    const SLOW_MS = 2000;
    const runId = Math.random().toString(36).slice(2, 8);
    const overallStart = performance.now();
    console.info(`[my-membership:${runId}] load start`);

    const reportSlow = (label: string, duration: number, extra?: Record<string, unknown>) => {
      const payload = { label, duration_ms: Math.round(duration), runId, ...extra };
      console.warn(`[my-membership:${runId}] slow ${label} (${Math.round(duration)}ms)`, payload);
      const sentry = (window as any).Sentry;
      if (sentry?.captureMessage) {
        try {
          sentry.captureMessage(`my-membership slow ${label}`, {
            level: "warning",
            extra: payload,
          });
        } catch {
          /* ignore */
        }
      }
    };

    const reportError = (label: string, err: unknown, duration: number) => {
      const payload = {
        label,
        duration_ms: Math.round(duration),
        runId,
        message: err instanceof Error ? err.message : String(err),
      };
      console.error(`[my-membership:${runId}] ${label} failed`, payload, err);
      const sentry = (window as any).Sentry;
      if (sentry?.captureException) {
        try {
          sentry.captureException(err, { tags: { area: "my-membership", label }, extra: payload });
        } catch {
          /* ignore */
        }
      }
    };

    const timed = async <T,>(label: string, run: () => PromiseLike<T>): Promise<T> => {
      const start = performance.now();
      try {
        const result = await run();
        const dur = performance.now() - start;
        if (dur >= SLOW_MS) reportSlow(label, dur);
        else console.debug(`[my-membership:${runId}] ${label} ok (${Math.round(dur)}ms)`);
        return result;
      } catch (err) {
        reportError(label, err, performance.now() - start);
        throw err;
      }
    };

    // Resolves with fallback on timeout/error — for non-critical calls.
    const withTimeout = <T,>(p: PromiseLike<T>, ms: number, fallback: T, label: string): Promise<T> =>
      new Promise((resolve) => {
        const start = performance.now();
        const timer = setTimeout(() => {
          reportSlow(`${label} (fallback after timeout)`, performance.now() - start, {
            timeout_ms: ms,
          });
          resolve(fallback);
        }, ms);
        Promise.resolve(p).then(
          (v) => {
            clearTimeout(timer);
            const dur = performance.now() - start;
            if (dur >= SLOW_MS) reportSlow(label, dur);
            resolve(v);
          },
          (err) => {
            clearTimeout(timer);
            reportError(label, err, performance.now() - start);
            resolve(fallback);
          },
        );
      });

    // Rejects on timeout — for critical calls so errors flow to catch().
    const withDeadline = <T,>(p: PromiseLike<T>, ms: number, label: string): Promise<T> =>
      new Promise((resolve, reject) => {
        const start = performance.now();
        const timer = setTimeout(() => {
          const err = new Error(`${label} timed out after ${ms}ms`);
          reportError(label, err, performance.now() - start);
          reject(err);
        }, ms);
        Promise.resolve(p).then(
          (v) => {
            clearTimeout(timer);
            const dur = performance.now() - start;
            if (dur >= SLOW_MS) reportSlow(label, dur);
            resolve(v);
          },
          (e) => {
            clearTimeout(timer);
            reportError(label, e, performance.now() - start);
            reject(e);
          },
        );
      });

    // Absolute safety net: stop the spinner after 12s regardless.
    const hardTimeout = setTimeout(() => {
      if (!cancelled) {
        reportSlow("overall (hard timeout)", performance.now() - overallStart, {
          hard_timeout_ms: 12000,
        });
        setTimedOut(true);
        setLoading(false);
      }
    }, 12000);

    (async () => {
      try {
        const { data: userResp, error: userErr } = await timed("getUser", () =>
          withDeadline(supabase.auth.getUser(), 6000, "getUser"),
        );
        if (userErr) throw userErr;
        const user = userResp.user;
        if (!user) {
          if (!cancelled) {
            setSignedIn(false);
            setLoading(false);
          }
          return;
        }

        const profileRes = await timed("profile", () =>
          withDeadline(
            supabase
              .from("profiles")
              .select(
                "display_name, first_name, last_name, avatar_url, membership_tier, joined_at, created_at",
              )
              .eq("user_id", user.id)
              .maybeSingle(),
            6000,
            "profile",
          ),
        );
        if (profileRes.error) throw profileRes.error;
        const profile = profileRes.data;

        // Role lookup is non-critical — default to Member on failure.
        const adminRes = await withTimeout(
          supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
          6000,
          { data: false, error: null } as any,
          "has_role",
        );
        const isAdmin = adminRes?.data ?? false;

        const fullName =
          (profile?.display_name && profile.display_name.trim()) ||
          [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
          user.email?.split("@")[0] ||
          "Friend";
        const role: RoleLabel = isAdmin ? "Admin" : "Member";
        if (!cancelled) {
          setSignedIn(true);
          setSummary({
            email: user.email ?? null,
            name: fullName,
            role,
            joined: profile?.joined_at ?? profile?.created_at ?? user.created_at ?? null,
            tier: profile?.membership_tier ?? "free",
            avatarUrl: profile?.avatar_url ?? null,
          });
        }
        const total = performance.now() - overallStart;
        if (total >= SLOW_MS) reportSlow("overall", total);
        else console.info(`[my-membership:${runId}] load ok (${Math.round(total)}ms)`);
      } catch (err) {
        reportError("overall", err, performance.now() - overallStart);
        if (!cancelled) {
          setErrorMsg(
            err instanceof Error && err.message
              ? err.message
              : "We couldn't load your account.",
          );
        }
      } finally {
        clearTimeout(hardTimeout);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(hardTimeout);
    };
  }, [reloadKey]);

  const retry = () => setReloadKey((n) => n + 1);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:py-14">
      <header className="mb-10 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-light/40 px-3 py-1 text-xs font-medium text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Your member area
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          My Membership
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground/80">
          {!loading && !signedIn
            ? "Sign in to see your membership, saved resources and events — everything in one place."
            : "Welcome back. Everything you need for your Recovery Trauma™ journey is here in one place."}
        </p>
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Manage your profile, membership, saved resources, workshops and
          personal preferences.
        </p>
      </header>

      {loading ? (
        <div className="mb-8 flex items-center justify-center rounded-2xl border border-border/60 bg-card p-10 text-sm text-muted-foreground">
          <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading your account…
        </div>
      ) : !signedIn ? (
        <SignedOutCard />
      ) : errorMsg ? (
        <div
          role="alert"
          className="mb-8 flex flex-col items-center justify-center gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-8 text-center"
        >
          <p className="text-sm font-medium text-foreground">
            We couldn't load your account.
          </p>
          <p className="max-w-md text-sm text-muted-foreground">{errorMsg}</p>
          <button
            type="button"
            onClick={retry}
            className="mt-1 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold-light/40 px-4 py-2 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold-light/60"
          >
            Try again
          </button>
        </div>
      ) : timedOut ? (
        <div className="mb-8 flex flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card p-8 text-center">
          <p className="text-sm font-medium text-foreground">
            This is taking longer than usual.
          </p>
          <p className="max-w-md text-sm text-muted-foreground">
            We couldn't finish loading your account. Please check your connection
            and try again.
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-1 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold-light/40 px-4 py-2 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold-light/60"
          >
            Try again
          </button>
        </div>
      ) : summary ? (
        <>
          <ProfileCard summary={summary} />
          <MembershipCard summary={summary} />
        </>
      ) : null}



      <h2 className="mb-5 mt-2 font-display text-xl font-semibold text-foreground">
        Quick Access
      </h2>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className="group relative flex flex-col gap-4 rounded-3xl border border-border/60 bg-card p-7 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all duration-300 ease-out hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_18px_40px_-20px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/40 bg-gold-light/30 text-[var(--gold-deep)] transition-colors group-hover:border-gold/60 group-hover:bg-gold-light/50">
                  <Icon
                    className={`h-[22px] w-[22px] ${item.iconClassName ?? ""}`}
                    strokeWidth={1.5}
                    fill={item.iconFill ?? "none"}
                  />
                </div>
                <ArrowRight
                  className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-all group-hover:translate-x-0.5 group-hover:text-[var(--gold-deep)]"
                  strokeWidth={1.5}
                />
              </div>
              <div>
                <h3 className="font-display text-base font-semibold text-foreground">
                  {item.label}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {item.desc}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      <FeedbackSection />
    </div>
  );
}

function SignedOutCard() {
  return (
    <div className="mb-8 rounded-3xl border border-gold/40 bg-gradient-to-br from-cream/60 to-card p-8 text-center">
      <Heart
        className="mx-auto mb-3 h-7 w-7 text-[var(--gold-deep)]"
        strokeWidth={1.5}
      />
      <h2 className="font-display text-xl font-semibold text-foreground">
        Sign in to see your profile & membership
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Your account home holds your profile details, membership status, and
        billing — please sign in to view it.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <Link to="/login" className="btn-gold text-sm">
          Sign in <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </Link>
        <Link
          to="/membership"
          className="inline-flex items-center gap-2 rounded-xl border border-gold/40 bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-gold-light/30"
        >
          Join as Member
        </Link>
      </div>
    </div>
  );
}

function ProfileCard({ summary }: { summary: Summary }) {
  const RoleIcon =
    summary.role === "Admin"
      ? Shield
      : summary.role === "Therapist"
        ? Stethoscope
        : UserRound;
  return (
    <section className="mb-5 rounded-3xl border border-border/60 bg-card p-7">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-foreground">
          Profile
        </h2>
        <Link
          to="/profile"
          className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-gold-light/30"
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /> Edit profile
        </Link>
      </div>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gold-light/40 ring-1 ring-gold/40">
          {summary.avatarUrl ? (
            <img loading="lazy" decoding="async" src={summary.avatarUrl} alt={`${summary.name} profile picture`} className="h-full w-full object-cover" />
          ) : (
            <UserCircle2
              className="h-8 w-8 text-[var(--gold-deep)]"
              strokeWidth={1.5}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-xl font-semibold text-foreground">
            {summary.name}
          </p>
          <p className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {summary.email ?? "—"}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-gold-light/50 px-2.5 py-1 font-semibold text-[var(--gold-deep)]">
              <RoleIcon className="h-3 w-3" strokeWidth={1.5} /> {summary.role}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-muted-foreground">
              <CalendarDays className="h-3 w-3" strokeWidth={1.5} /> Member since{" "}
              {summary.joined
                ? new Date(summary.joined).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "—"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

type MembershipRow = Awaited<ReturnType<typeof getMyMembership>>;

type StateMeta = {
  label: string;
  tone: "success" | "warning" | "danger" | "neutral" | "info";
  description: string;
};

function stateMetaFor(row: MembershipRow, isAdmin: boolean): StateMeta {
  if (isAdmin) {
    return {
      label: "Complimentary",
      tone: "info",
      description: "Admin access — no billing on this account.",
    };
  }
  if (!row) {
    return {
      label: "Not active",
      tone: "neutral",
      description:
        "You don't have an active membership yet. Visit Membership to choose a plan.",
    };
  }
  switch (row.status) {
    case "active":
      return row.cancel_at_period_end
        ? {
            label: "Cancels at period end",
            tone: "warning",
            description:
              "Your membership stays active until the current period ends, then it won't renew.",
          }
        : {
            label: "Active",
            tone: "success",
            description: "Your membership is active and renewing normally.",
          };
    case "pending_activation":
      return row.paid_at
        ? {
            label: "Pending activation",
            tone: "info",
            description:
              "We've received your payment and are finalising your membership. This usually completes within a few minutes.",
          }
        : {
            label: "Payment not completed",
            tone: "warning",
            description:
              "Your account is created but payment hasn't been completed yet. Click below to complete your Membership and unlock the platform.",
          };

    case "payment_failed":
      return {
        label: "Payment failed",
        tone: "danger",
        description:
          "The last payment didn't go through. Please update your card in the billing portal to keep your membership active.",
      };
    case "cancelled":
      return {
        label: "Cancelled",
        tone: "neutral",
        description: "Your membership has been cancelled.",
      };
    case "expired":
      return {
        label: "Expired",
        tone: "neutral",
        description:
          "Your membership has ended. Renew any time to unlock the full experience again.",
      };
    default:
      return { label: row.status, tone: "neutral", description: "" };
  }
}

const TONE_CLASS: Record<StateMeta["tone"], string> = {
  success: "bg-emerald-100 text-emerald-700 [&_.dot]:bg-emerald-500",
  warning: "bg-amber-100 text-amber-800 [&_.dot]:bg-amber-500",
  danger: "bg-rose-100 text-rose-700 [&_.dot]:bg-rose-500",
  info: "bg-sky-100 text-sky-700 [&_.dot]:bg-sky-500",
  neutral: "bg-muted text-muted-foreground [&_.dot]:bg-muted-foreground/60",
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function MembershipCard({ summary }: { summary: Summary }) {
  const fetchMine = useServerFn(getMyMembership);
  const [row, setRow] = useState<MembershipRow | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const isAdmin = summary.role === "Admin";

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    fetchMine({ data: undefined as never })
      .then((data) => {
        if (cancelled) return;
        setRow(data);
        setState("ready");
      })
      .catch((err) => {
        console.error("[my-membership] getMyMembership failed", err);
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [fetchMine]);

  const nativeApp = useIsNativeApp();
  const meta = stateMetaFor(row, isAdmin);
  const isPaidMember =
    row?.status === "active" ||
    summary.tier === "founding" ||
    summary.tier === "healing";
  const planSource = {
    stripe_price_id: (row as any)?.stripe_price_id ?? null,
    paypal_plan_id: (row as any)?.paypal?.plan_id ?? null,
    amount_paid_cents: (row as any)?.amount_paid_cents ?? null,
    complimentary_grant: (row as any)?.complimentary_grant ?? null,
  };
  const planLabel = describePlan(planSource, isPaidMember);
  const grantReason = String(
    (planSource.complimentary_grant as { reason?: string } | null)?.reason ?? "",
  );
  const isReviewAccount = /app review/i.test(grantReason);
  const tierHint = isAdmin
    ? "Admin Access"
    : isReviewAccount
      ? "Demo Access"
      : (planLabel ??
        (planSource.complimentary_grant
          ? "Complimentary Access"
          : isPaidMember
            ? "Member"
            : "Membership"));
  const paypal = row?.paypal ?? null;
  const hasPaypal = !!paypal?.paypal_subscription_id;
  const hasStripeSub = !!row?.stripe_subscription_id;
  const renewLabel = row?.cancel_at_period_end ? "Ends" : "Renews";
  const renewValue = formatDate(
    row?.current_period_end ??
      row?.next_billing_date ??
      paypal?.next_billing_time ??
      null,
  );
  const activatedValue = formatDate(
    row?.activated_at ?? row?.paid_at ?? paypal?.start_time ?? null,
  );
  const cancelledValue = formatDate(row?.cancelled_at ?? null);
  const term = resolveBillingTerm(planSource);
  const hasSubscription = hasStripeSub || hasPaypal;
  const billingCycle = hasSubscription ? term.termLabel : "—";
  const amountValue = hasSubscription ? term.priceLabel : "—";

  const hasStripe = !!row?.stripe_customer_id;
  const provider = resolveBillingProvider(row as never);
  const appleManaged = isAppleManaged(provider);
  const billingProvider = provider ? PROVIDER_LABEL[provider] : null;
  const providerHint = manageSubscriptionHint(provider);
  // Access and renewal are separate: a cancelled membership that is paid up
  // keeps access until the paid-through date.
  const accessStatusLabel =
    row?.status === "active"
      ? `Active${renewValue !== "—" ? ` until ${renewValue}` : ""}`
      : meta.label;
  const renewalStatusLabel = !row
    ? "—"
    : row.status === "active"
      ? row.cancel_at_period_end
        ? "Cancelled — will not renew"
        : "Renewing"
      : row.status === "payment_failed"
        ? "Billing issue — payment retrying"
        : provider === "complimentary"
          ? "Complimentary — no renewal"
          : "Not renewing";
  const billingEmail = paypal?.paypal_payer_email ?? row?.email ?? null;

  return (
    <section className="mb-10 rounded-3xl border border-gold/40 bg-gradient-to-br from-cream/60 to-card p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-[var(--gold-deep)]" strokeWidth={1.5} />
          <h2 className="font-display text-lg font-semibold text-foreground">
            Membership &amp; Billing
          </h2>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${TONE_CLASS[meta.tone]}`}
        >
          <span className="dot h-1.5 w-1.5 rounded-full" />
          {state === "loading" ? "Loading…" : meta.label}
        </span>
      </div>

      {state === "error" ? (
        <p className="mt-4 text-sm text-muted-foreground">
          We couldn't load your billing details just now. Please refresh and try again.
        </p>
      ) : (
        <>
          {meta.description && (
            <p className="mt-3 text-sm text-foreground/80">{meta.description}</p>
          )}

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <Stat label="Plan" value={tierHint} />
            <Stat label="Billing cycle" value={billingCycle} />
            <Stat label="Amount" value={amountValue} />
            <Stat label={renewLabel} value={renewValue} />
            <Stat
              label={row?.status === "cancelled" ? "Cancelled" : "Member since"}
              value={row?.status === "cancelled" ? cancelledValue : activatedValue}
            />
          </div>

          {hasSubscription && row?.status === "active" && (
            <p className="mt-3 text-xs text-muted-foreground">
              {row.cancel_at_period_end
                ? `This is a recurring subscription. It won't renew — your access continues until ${renewValue}.`
                : `This is a recurring subscription. ${amountValue.replace(" / ", " is charged every ")} via ${billingProvider ?? "your payment provider"}, next on ${renewValue}. You can cancel any time.`}
            </p>
          )}


          {(billingEmail || billingProvider) && (
            <div className="mt-4 space-y-2 rounded-xl border border-border/60 bg-background/60 px-4 py-3 text-sm">
              {billingProvider && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Billing provider</span>
                  <span className="font-medium text-foreground">{billingProvider}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Access status</span>
                <span className="font-medium text-foreground">{accessStatusLabel}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Renewal status</span>
                <span className="font-medium text-foreground">{renewalStatusLabel}</span>
              </div>
              {providerHint && (
                <p className="pt-1 text-xs text-muted-foreground">{providerHint}</p>
              )}
              {billingEmail && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Billing email</span>
                  <span className="truncate font-medium text-foreground">
                    {billingEmail}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {!nativeApp && (
              <Link
                to="/billing"
                className="inline-flex items-center gap-2 rounded-xl border border-gold/40 bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-gold-light/30"
              >
                View billing details <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            )}
            {!nativeApp && !row && !isAdmin && (
              <Link to="/membership" className="btn-gold text-sm">
                Join as Member
              </Link>
            )}
            {appleManaged && (
              <a
                href={APPLE_SUBSCRIPTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold text-sm"
              >
                Manage Subscription with Apple
              </a>
            )}
            {!nativeApp && !appleManaged && row?.status === "payment_failed" && hasStripe && (
              <Link to="/billing" className="btn-gold text-sm">
                Update payment method
              </Link>
            )}
            {!nativeApp &&
              !appleManaged &&
              row?.status === "active" &&
              !row.cancel_at_period_end &&
              !isAdmin && (
                <Link
                  to="/billing"
                  search={{ cancel: "1" }}
                  data-testid="cancel-membership-link"
                  onClick={() =>
                    trackOperational("cancel_membership_clicked", {
                      from: "my_membership",
                      at: new Date().toISOString(),
                    })
                  }
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground transition hover:text-foreground"
                >
                  Cancel membership
                </Link>
              )}
          </div>

          {/* Inside the app the billing page is unavailable, so cancellation
              and the route to account deletion live here. */}
          {nativeApp && !appleManaged && row && (
            <InAppCancelMembership
              status={row.status ?? null}
              cancelAtPeriodEnd={!!row.cancel_at_period_end}
              accessEndsAt={
                row.current_period_end ??
                row.next_billing_date ??
                paypal?.next_billing_time ??
                null
              }
              canCancel={
                !isAdmin &&
                (row.status === "active" || row.status === "payment_failed") &&
                hasSubscription
              }
              onCancelled={() => {
                fetchMine({ data: undefined as never })
                  .then(setRow)
                  .catch(() => undefined);
              }}
            />
          )}
          {nativeApp && appleManaged && (
            <p className="mt-4 text-sm text-muted-foreground">
              This membership is billed by Apple, so it is cancelled in your Apple
              subscription settings. Once cancelled you can delete your account under{" "}
              <Link to="/account/privacy" className="underline">
                Privacy &amp; data
              </Link>
              .
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/60 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

function FeedbackSection() {
  const [submitted, setSubmitted] = useState(false);
  return (
    <section className="mt-12 rounded-3xl border border-border/60 bg-card p-7 text-center">
      <h3 className="font-display text-lg font-semibold text-foreground">
        Was this page helpful today?
      </h3>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
        Your gentle feedback shapes how Recovery Trauma™ supports you.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={() => {
            setSubmitted(true);
            toast.success("Thank you — noted with care.");
          }}
          disabled={submitted}
          className="inline-flex items-center gap-2 rounded-xl border border-gold/40 bg-background px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-gold/60 hover:bg-gold-light/30 disabled:opacity-60"
        >
          <ThumbsUp className="h-4 w-4 text-[var(--gold-deep)]" strokeWidth={1.5} />
          {submitted ? "Thank you" : "Helpful"}
        </button>
        <Link
          to="/support"
          className="inline-flex items-center gap-2 rounded-xl border border-gold/40 bg-background px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-gold/60 hover:bg-gold-light/30"
        >
          <MessageSquare
            className="h-4 w-4 text-[var(--gold-deep)]"
            strokeWidth={1.5}
          />
          Share Feedback
        </Link>
      </div>
    </section>
  );
}
